import {
  Component,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { get } from 'lodash';
import { Subscription } from 'rxjs/Subscription';
import { combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import * as actions from 'redux/actions';
import { Search } from 'app/models';
import { Paginator } from 'primeng/paginator';
import { Content, EXPORT_TIMEOUT_MESSAGE } from 'app/constants';
import { SearchService } from 'app/services';
import { Notification } from 'app/models/notification.model';
import { ExportFilenameService } from 'app/components';

@Component({
  selector: 'app-coverage',
  templateUrl: './coverage.component.html',
  styleUrls: ['./coverage.component.scss'],
})
export class CoverageComponent implements OnInit, OnDestroy {
  private topicStoreSub: Subscription;
  private coveragesSub: Subscription;
  private searchSub: Subscription;
  private uiSub: Subscription;
  public topicId: number;
  public currentPage: number;
  public page: number;
  public pageSize: number;
  public feeds: any[] = [];
  public totalPages: number;
  public total: number;
  public totalItems: number;
  public isSearch: boolean;
  public filterSearch: Search;
  public searchLoaded: boolean;
  public loading: boolean;
  public keywords: string;
  public coverageTitle = 'Coverage';
  public saveAsTopicLabel: string;
  public reportMissingLabel: string;
  public recentSortLabel: string;
  public relevantSortLabel: string;
  public sortSelected: string;
  public parentName: string;
  public hideCoverageTitle: boolean;
  public isLoadingContent: boolean;
  public showPageButton: boolean;
  public screenSize: number;
  public showSort: boolean;
  public isExportingExcel: boolean;
  public isExportingText: boolean;
  public selectedExportValue: string;
  public exportOptions: any[] = [
    { label: 'Export', value: 'placeholder', icon: 'pi pi-download', disabled: true },
    {
      label: 'Export to Excel',
      value: 'excel',
      icon: 'pi pi-fw pi-file-excel',
    },
    {
      label: 'Export to Text',
      value: 'text',
      icon: 'pi pi-fw pi-file',
    },
  ];

  @Output() reportMissingDataEmitter: EventEmitter<any> = new EventEmitter();
  @ViewChild('pp', { static: false }) paginator: Paginator;

  constructor(
    private store: Store<AppState>,
    private searchService: SearchService,
    private exportFilenameService: ExportFilenameService
  ) {
    this.parentName = Content.Coverage;
    this.currentPage = 0;
    this.sortSelected = 'recent';
    this.page = 1;
    this.pageSize = 50;
    this.isSearch = false;
    this.searchLoaded = false;
    this.keywords = null;
    this.loading = false;
    this.saveAsTopicLabel = 'Save as topic';
    this.reportMissingLabel = 'Report Missing Data';
    this.recentSortLabel = 'Recent';
    this.relevantSortLabel = 'Relevant';
    this.hideCoverageTitle = false;
    this.showPageButton = true;
    this.showSort = true;
    this.isExportingExcel = false;
    this.isExportingText = false;
    this.selectedExportValue = null;
  }

  ngOnDestroy(): void {
    if (this.topicStoreSub) {
      this.topicStoreSub.unsubscribe();
    }
    if (this.coveragesSub) {
      this.coveragesSub.unsubscribe();
    }
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    if (this.uiSub) {
      this.uiSub.unsubscribe();
    }
  }

  ngOnInit() {
    this.uiSub = this.store
      .select('ui')
      .subscribe(({ contemItemPanelSize, screenSizeValues }) => {
        this.screenSize = screenSizeValues.scrWidth;
        if (this.screenSize < 1386) {
          this.saveAsTopicLabel = '..';
          this.reportMissingLabel = '..';
          this.hideCoverageTitle = true;
        } else {
          this.saveAsTopicLabel = 'Save as topic';
          this.reportMissingLabel = 'Report Missing Data';

          this.hideCoverageTitle = false;
        }

        if (this.screenSize >= 1500) {
          this.showPageButton = true;
          this.recentSortLabel = 'Rec..';
          this.relevantSortLabel = 'Rel..';
        } else {
          this.showPageButton = false;
          this.recentSortLabel = 'Recent';
          this.relevantSortLabel = 'Relevant';
        }

        if (this.screenSize <= 1200) {
          this.showSort = false;
        } else {
          this.showSort = true;
        }
      });

    this.searchSub = combineLatest([
      this.store.select('search'),
      this.store.select('authorContactDetails'),
      this.store.select('outletContactDetails'),
      this.store.select('topicDetails'),
    ]).subscribe(([{ searchBody, loaded }, authorDetails, outletDetails, topicDetails]) => {
        this.filterSearch = searchBody;
        this.searchLoaded = loaded;
        this.keywords = searchBody?.keywords;
        const author = searchBody?.authors?.length === 1 && authorDetails.authorContactProfile;
        const outlet = searchBody?.outlets?.length === 1 && outletDetails.outletContactProfile;
        const topic = searchBody?.topics?.length === 1 && topicDetails.topicContactProfile;
        const name = (author || outlet || topic)?.name || this.keywords?.trim().replace(/^"(.*)"$/, '$1');
        this.coverageTitle = author || outlet ? `Coverage by ${name}` : `Coverage of ${name}`;
      });

    this.topicStoreSub = this.store.select('topic').subscribe(({ topic }) => {
      this.topicId = get(topic, 'id', null);
    });

    this.coveragesSub = this.store
      .select('coverages')
      .subscribe(({ contents, isSearch, loaded, loading }) => {
        this.isLoadingContent = loading;
        this.loading = loaded;
        this.feeds = [];
        const contentItems = get(contents, 'content', []);
        this.isSearch = isSearch;
        if (loaded) {
          if (contentItems.length > 0) {
            this.feeds = contentItems;
            this.currentPage = contents.number;
            this.page = contents.number + 1;
            this.totalPages = contents.totalPages;
            this.total = contents.totalElements;
          } else {
            this.feeds = [];
            this.currentPage = 0;
            this.page = 0;
            this.totalPages = 0;
            this.total = 0;
          }
        }
        // this.scrollTop();
      });
  }

  public handleSaveSearchAsTopic() {
    this.store.dispatch(actions.setSaveAsTopic());
    this.store.dispatch(actions.showSidebar());
  }

  openReportMissingDataModal() {
    // this.reportMissingDataEmitter.emit('coverage');
    this.store.dispatch(actions.showReportDialog());
  }

  private getContentItems() {
    this.store.dispatch(
      actions.setCoverages({
        searchBody: this.filterSearch,
        topicId: this.topicId,
        sort: this.sortSelected,
        page: this.currentPage,
        size: this.pageSize,
        isSearch: this.isSearch,
      })
    );
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.currentPage = 0;
    this.page = 1;
    this.resetPaginator();
    this.getContentItems();
  }

  getPage({ page }) {
    this.currentPage = page;
    this.page = page + 1;
    this.getContentItems();
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }

  public handleExport(event: any) {
    const exportType = event.value;
    const extension = exportType === 'excel' ? 'xls' : 'txt';

    this.exportFilenameService
      .prompt(this.getDefaultExportFilename(extension))
      .subscribe((filename) => {
        if (!filename) {
          this.selectedExportValue = null;
          return;
        }

        this.selectedExportValue = exportType;
        if (exportType === 'excel') {
          this.isExportingExcel = true;
          this.handleExportToExcel(filename);
        } else if (exportType === 'text') {
          this.isExportingText = true;
          this.handleExportToText(filename);
        }
      });
  }

  private getDefaultExportFilename(extension: 'xls' | 'txt'): string {
    const date = new Date();
    const formattedDate = `${date.getFullYear()}${String(
      date.getMonth() + 1
    ).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    return `TechNews_Search_${formattedDate}.${extension}`;
  }

  private handleExportToExcel(filename: string) {
    this.searchService.coverageExportToExcel(this.filterSearch).subscribe({
      next: (response: Blob) => {
        if (response && response.size > 0) {
          const blob = new Blob([response], {
            type: 'application/vnd.ms-excel',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.isExportingExcel = false;
            this.selectedExportValue = null;
          }, 100);
        } else {
          this.isExportingExcel = false;
          this.selectedExportValue = null;
          this.store.dispatch(
            actions.showNotification({
              notify: new Notification(
                'No data received from server',
                'error',
                'tr'
              ),
            })
          );
        }
      },
      error: (error) => {
        this.isExportingExcel = false;
        this.selectedExportValue = null;
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification(
              error?.name === 'TimeoutError' ? EXPORT_TIMEOUT_MESSAGE : 'Error exporting to Excel', 'error', 'tr'),
          })
        );
      },
    });
  }

  private handleExportToText(filename: string) {
    this.searchService.coverageExportToText(this.filterSearch).subscribe({
      next: (response: string) => {
        const blob = new Blob([response], {
          type: 'text/plain;charset=utf-8',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.isExportingText = false;
          this.selectedExportValue = null;
        }, 100);
      },
      error: (error) => {
        this.isExportingText = false;
        this.selectedExportValue = null;
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification(
              error?.name === 'TimeoutError' ? EXPORT_TIMEOUT_MESSAGE : 'Error exporting to text',
              'error',
              'tr'
            ),
          })
        );
      },
    });
  }
}
